import useSWR from 'swr';
import api from '../../services/api';

export interface Conversation {
  id: number;
  conversationType: 'TICKET' | 'TASK';
  ticketId?: number;
  taskId?: number;
  message: string;
  senderType: 'STAFF' | 'CLIENT';
  staffUserId?: number;
  clientId?: number;
  replyToId?: number;
  isRead: boolean;
  createdAt: string;
  updatedAt?: string;
  staffUser?: {
    id: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string;
  };
  client?: {
    clientId: number;
    clientName?: string;
  };
  replyTo?: {
    id: number;
    message: string;
    senderType: 'STAFF' | 'CLIENT';
    createdAt: string;
  };
  replies?: Conversation[];
  attachments?: Array<{
    id: number;
    fileName: string;
    filePath: string;
    fileType: string;
  }>;
}

export interface CreateConversationData {
  conversationType: 'TICKET' | 'TASK';
  ticketId?: number;
  taskId?: number;
  message: string;
  senderType: 'STAFF' | 'CLIENT';
  staffUserId?: number;
  clientId?: number;
  replyToId?: number;
}

export const useConversations = (ticketId?: number, taskId?: number) => {
  const params = new URLSearchParams();
  if (ticketId) params.append('ticketId', String(ticketId));
  if (taskId) params.append('taskId', String(taskId));

  const { data, error, isLoading, mutate } = useSWR<Conversation[]>(
    `/conversations${params.toString() ? `?${params.toString()}` : ''}`,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    conversations: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useConversation = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Conversation>(
    id ? `/conversations/${id}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    conversation: data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useConversationsByTicket = (ticketId: number) => {
  const { data, error, isLoading, mutate } = useSWR<Conversation[]>(
    ticketId ? `/conversations/ticket/${ticketId}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    conversations: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useConversationsByTask = (taskId: number) => {
  const { data, error, isLoading, mutate } = useSWR<Conversation[]>(
    taskId ? `/conversations/task/${taskId}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    conversations: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const createConversation = async (data: CreateConversationData): Promise<Conversation> => {
  const response = await api.post('/conversations', data);
  return response.data;
};

export const updateConversation = async (id: number, data: Partial<CreateConversationData>): Promise<Conversation> => {
  const response = await api.patch(`/conversations/${id}`, data);
  return response.data;
};

export const markAsRead = async (id: number): Promise<void> => {
  await api.patch(`/conversations/${id}/mark-read`);
};

export const deleteConversation = async (id: number): Promise<void> => {
  await api.delete(`/conversations/${id}`);
};
